import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { gql } from '@/__generated__';
import { ListFormInputs } from '@/components/stream-deck/list-form/list-form';

const UPDATE_CUSTOM_COLLECTION = gql(`
  mutation UpdateCustomCollection($input: UpdateCustomCollectionInput!) {
    updateCustomCollection(input: $input) {
      ...CustomCollectionFragment
    }
  }
`);

export const useEditCustomCollection = (shouldNotifyOnSuccess = false) => {
  const toast = useToast();

  const [updateCustomCollectionMutation, { loading }] = useMutation(
    UPDATE_CUSTOM_COLLECTION,
    {
      onCompleted: () => {
        if (shouldNotifyOnSuccess) {
          toast.success({
            title: 'List updated successfully',
          });
        }
      },
      onError: () => {
        toast.error({
          title: 'Error while updating the list',
          subTitle: 'Please try again later',
        });
      },
    },
  );

  const editCustomCollection = async ({
    id,
    name,
    sources,
    sourceCustomTargets,
    streamSourceIds,
    filters,
    streamDeckId,
    highlights,
  }: ListFormInputs & {
    id: string;
    streamDeckId: string;
  }) => {
    return updateCustomCollectionMutation({
      variables: {
        input: {
          id,
          streamDeckId,
          name,
          sources: sources.map((s) => ({
            streamId: s.id,
            customTargets: sourceCustomTargets?.[s.id]?.length
              ? sourceCustomTargets[s.id]
              : null,
            sourceIds: streamSourceIds?.[s.id]?.length
              ? streamSourceIds[s.id]
              : null,
          })),
          highlights,
          filters: { ...filters, ai: filters.ai?.id },
        },
      },
      update(cache, result) {
        const updatedCollection = result.data?.updateCustomCollection;
        if (updatedCollection) {
          cache.evict({
            fieldName: 'customCollectionPosts',
            args: { collectionId: updatedCollection.id },
          });
          cache.evict({
            fieldName: 'aiFilters',
          });
          cache.gc();
        }
      },
    });
  };

  return {
    editCustomCollection,
    loading,
  };
};
